/**
 * Pointer interaction of the motion columns (W2-04a; DESIGN.md §4.7, §5.6.1-5.6.2, §8.2 mouse table, §8.3):
 *
 *  - press on a key / half-diamond / segment: focuses the column (`setFocusedColumn`: K, G, Ctrl+A act on it) and
 *    selects (Shift add, Ctrl/Cmd toggle; a press on a selected key keeps the selection for a multi-key drag);
 *  - drag a key: vertical = time, horizontal = value, as ONE live gesture (preview, lane and inspector follow):
 *    time snaps to the beat grid (Alt, or Ctrl+Shift in the Alt-free variant: no snap), values round to the pixel
 *    precision (Ctrl: round numbers), Shift locks the axis chosen by the first 4 px; every selected key moves in
 *    time with it (also in other columns and lines), the keys of the grabbed column also change value. The core
 *    clamps keys between their neighbours (one step before them) and mirrors linked Position X/Y moves; the value
 *    axis is frozen while dragging. In a Guided Σ column the first drag asks the "uses N layers" question instead;
 *  - drag a half-diamond of a jump key: vertical = time as for a key (with the selection), horizontal = the
 *    arrive (vIn) or leave (vOut) value only, so the grabbed half picks which value changes; Esc cancels any drag
 *    (the host's cancel);
 *  - press on empty space: a marquee across columns (the host runs it and merges every column's keys);
 *  - double-click: on empty space a key at that beat that keeps the motion (shape-preserving split); on a segment
 *    the easing picker; on a key the inspector's value field;
 *  - right-click: selects what is under the pointer and opens the "motion.context" menu there;
 *  - RPE preset: Ctrl+wheel over a motion column fine-tunes the selected keyframes (EVT-11, undoable, coalesced);
 *  - value axis (DESIGN.md §8.2 mouse table): Ctrl+Shift+wheel zooms the value range about the pointer, a horizontal
 *    wheel and a middle drag pan it (the host pans time). Both lock the lane's range ("Lock the value range" in the
 *    column menu, unticked = auto-fit again).
 * Hover moves update the hover ring and the pointer position that RPE R…R placement and paste ghosts use.
 */

import { Core } from '../../core/commands/index';
import { clampToDomain } from '../../core/commands/keyValueRules';
import type { KeyId, TrackValue } from '../../core/model/types';
import type { Beat } from '../../core/time/types';
import { Beats } from '../../core/time/beat';
import { formatBeat } from '../../core/time/beatSyntax';
import type { TimeAxis, TimelinePointerEvent } from '../../render/types';
import { FINEST_DIV, snapBeat } from '../../render/timeline/axis';
import type { MotionColumnHandlers, MotionColumnView } from '../../render/timeline/motion/motionColumn';
import { laneAtX, type ValueMap } from '../../render/timeline/motion/motionHit';
import type { MotionHit } from '../../render/timeline/motion/motionScene';
import type { ColumnRef, DocStore, Gesture } from '../../state/types';
import type { ColumnSpec } from './columnSpec';
import { laneKeyOf, laneRef, laneTrack } from './columnScene';
import { formatValue, valueStep } from './format';
import {
  addKeys,
  confirmGuidedEdit,
  confirmGuidedKeys,
  guidedPending,
  openEasingPicker,
  propCtxOf,
  refsOfKeys,
} from './motionActions';
import { m } from './motion.messages';
import type { MotionSession, ValueRange } from './session';
import { keysOrSegments, ref, runTx, snapDiv } from './txn';

/** Pointer travel before a press becomes a drag. */
const DRAG_PX = 3;
/** Travel that decides the Shift axis lock. */
const LOCK_PX = 4;

interface PressState {
  readonly view: MotionColumnView;
  readonly spec: ColumnSpec;
  readonly lane: number;
  readonly hit: MotionHit;
  readonly x0: number;
  readonly y0: number;
  readonly beat0: number;
  /** What a horizontal drag edits: the values of the grabbed lane's keys, or one half of a jump key. */
  readonly mode: 'keys' | 'in' | 'out' | 'none';
  /** Key under the pointer and its beat / values at the press. */
  readonly grabbed: KeyId | null;
  readonly grabbedBeat: Beat | null;
  /** Keys moved in time (selection at the press) and the value-edited keys of the grabbed track. */
  readonly keys: readonly KeyId[];
  /** Room to move, measured at the press. */
  readonly room: MoveRoom;
  readonly valueKeys: ReadonlyArray<{
    readonly key: KeyId;
    readonly vIn: number;
    readonly vOut: number;
    readonly jump: boolean;
  }>;
  /** Frozen value map of the grabbed lane. */
  readonly map: ValueMap | null;
  readonly laneKey: string;
  /** Click on an already selected key without modifiers: reduce the selection to it on release without a drag. */
  readonly collapseOnClick: boolean;
  moved: boolean;
  lock: 'time' | 'value' | null;
  gesture: Gesture | null;
}

/** Room around the moved keys, measured before a drag starts (the drag's own updates must not shrink it). */
export interface MoveRoom {
  /** Smallest gap from a moved key to the next unmoved key (null = none). */
  readonly after: Beat | null;
  /** Smallest gap from an unmoved previous key to a moved key (null = none). */
  readonly before: Beat | null;
  /** Smallest beat of a moved key that has no previous key (null = none): keys never go below beat 0. */
  readonly floor: Beat | null;
}

export function moveRoom(store: Pick<DocStore, 'doc' | 'idx'>, keys: readonly KeyId[]): MoveRoom {
  const moved = new Set(keys);
  let after: Beat | null = null;
  let before: Beat | null = null;
  let floor: Beat | null = null;
  for (const id of keys) {
    const k = store.doc.keys.get(id);
    if (k === undefined) continue;
    const next = store.idx.nextKey(id);
    const nk = next === undefined || moved.has(next) ? undefined : store.doc.keys.get(next);
    if (nk !== undefined) {
      const g = Beats.sub(nk.t, k.t);
      after = after === null ? g : Beats.min(after, g);
    }
    const prev = store.idx.prevKey(id);
    if (prev === undefined) floor = floor === null ? k.t : Beats.min(floor, k.t);
    else if (!moved.has(prev)) {
      const pk = store.doc.keys.get(prev);
      if (pk !== undefined) {
        const g = Beats.sub(k.t, pk.t);
        before = before === null ? g : Beats.min(before, g);
      }
    }
  }
  return { after, before, floor };
}

/**
 * Clamps a time move so the moved keys stop one grid step (1/div) before an unmoved neighbour and never pass beat 0
 * (DESIGN.md §4.7 "keys never pass each other"). The core clamps too; this keeps the stop on the user's grid.
 */
export function clampMove(room: MoveRoom, dt: Beat, div: number): Beat {
  if (Beats.eq(dt, Beats.ZERO)) return dt;
  const step = Beats.make(0, 1, Math.max(1, Math.round(div)));
  if (Beats.lt(Beats.ZERO, dt)) {
    if (room.after === null) return dt;
    return Beats.min(dt, Beats.max(Beats.sub(room.after, step), Beats.ZERO));
  }
  let eff = dt;
  if (room.before !== null) eff = Beats.max(eff, Beats.min(Beats.sub(step, room.before), Beats.ZERO));
  if (room.floor !== null) eff = Beats.max(eff, Beats.sub(Beats.ZERO, room.floor));
  return eff;
}

/** "Nice" rounding step for a raw value precision (1, 2, 5 x 10^k). */
export function niceStep(rawPerPx: number): number {
  if (!(rawPerPx > 0) || !Number.isFinite(rawPerPx)) return 1;
  const k = Math.floor(Math.log10(rawPerPx));
  const base = 10 ** k;
  for (const f of [1, 2, 5, 10]) if (f * base >= rawPerPx) return f * base;
  return 10 * base;
}

/** Rounds `v` to a multiple of `step` without binary noise. */
export function roundTo(v: number, step: number): number {
  if (!(step > 0)) return v;
  const r = Math.round(v / step) * step;
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
  return Number(r.toFixed(Math.min(12, decimals)));
}

/**
 * The "no snap" modifier of a drag: Alt, or Ctrl+Shift held in the Alt-free variant (DESIGN.md §8.1; some window
 * managers take Alt+drag). The same rule as the note lane.
 */
export function noSnapHeld(
  s: Pick<MotionSession['services'], 'keymap'>,
  e: { readonly alt: boolean; readonly ctrl: boolean; readonly shift: boolean },
): boolean {
  if (e.alt) return true;
  return altFreeChord(s, e);
}

/** Ctrl+Shift in the Alt-free variant (then neither the Shift axis lock nor the Ctrl rounding applies). */
function altFreeChord(
  s: Pick<MotionSession['services'], 'keymap'>,
  e: { readonly ctrl: boolean; readonly shift: boolean },
): boolean {
  if (!e.ctrl || !e.shift) return false;
  try {
    return s.keymap.altFree;
  } catch {
    return false;
  }
}

/** Smallest and largest value span a zoomed lane may show. */
const MIN_ZOOM_SPAN = 1e-6;
const MAX_ZOOM_SPAN = 1e9;

/**
 * The value range after zooming by `factor` (> 1 = zoom in) about `anchor` (the value under the pointer), clamped to
 * a sane span; null when nothing changes.
 */
export function zoomValueRange(lo: number, hi: number, factor: number, anchor: number): ValueRange | null {
  if (!(factor > 0) || !Number.isFinite(factor) || !(hi > lo)) return null;
  const a = Math.min(hi, Math.max(lo, anchor));
  const span = hi - lo;
  const next = Math.min(MAX_ZOOM_SPAN, Math.max(MIN_ZOOM_SPAN, span / factor));
  if (next === span) return null;
  const k = next / span;
  return { lo: a - (a - lo) * k, hi: a + (hi - a) * k };
}

export interface MotionGestureDeps {
  readonly session: MotionSession;
  specOf(id: string): ColumnSpec | null;
}

export function createMotionHandlers(deps: MotionGestureDeps): MotionColumnHandlers {
  const { session } = deps;
  let press: PressState | null = null;

  const svc = (): MotionSession['services'] => session.services;

  const setHover = (view: MotionColumnView, e: TimelinePointerEvent | null): void => {
    const prevId = session.hover?.instanceId ?? null;
    if (e === null) {
      session.hover = null;
    } else {
      const hit = view.hit(e.x, e.y);
      const lanes = view.lanes();
      const state = { instanceId: view.id, hit, lane: hit?.lane ?? laneAtX(lanes, e.x), beat: e.beat, x: e.x, y: e.y };
      session.hover = state;
      session.lastPointer = state;
    }
    if (prevId !== null && prevId !== view.id) session.invalidate('overlay', prevId);
    session.invalidate('overlay', view.id);
    const placement = svc().editor.getState().placement;
    if (placement !== null) session.invalidate('overlay');
  };

  const focusColumn = (view: MotionColumnView, spec: ColumnSpec, lane: number): void => {
    const l = spec.lanes[lane];
    if (l === undefined) return;
    const target = laneRef(spec, l);
    const st = svc().editor.getState();
    const cur = st.focusedColumn;
    if (cur === null || cur.line !== target.line || cur.prop !== target.prop || cur.layer !== target.layer)
      st.setFocusedColumn(target);
    const el = view.element();
    if (
      el !== null &&
      typeof el.focus === 'function' &&
      el.ownerDocument.activeElement !== el &&
      !el.contains(el.ownerDocument.activeElement)
    ) {
      try {
        el.focus({ preventScroll: true });
      } catch {
        // focus is best effort (detached element)
      }
    }
  };

  const selectHit = (hit: MotionHit, e: TimelinePointerEvent): boolean => {
    const st = svc().editor.getState();
    const sel = st.selection;
    if (hit.key === undefined) return false;
    const id = hit.key;
    if (hit.kind === 'segment') {
      const mode = e.ctrl ? 'toggle' : e.shift ? 'add' : 'replace';
      st.select({ segments: new Set([id]), primary: { kind: 'segment', id } }, mode);
      return false;
    }
    if (e.ctrl) {
      st.select({ keys: new Set([id]), primary: { kind: 'key', id } }, 'toggle');
      return false;
    }
    if (e.shift) {
      st.select({ keys: new Set([id]), primary: { kind: 'key', id } }, 'add');
      return false;
    }
    if (sel.keys.has(id)) {
      // Keep a multi-selection for dragging; reduce on a plain click (release without moving).
      if (sel.primary?.id !== id) st.select({ primary: { kind: 'key', id } }, 'add');
      return sel.keys.size > 1 || sel.notes.size > 0 || sel.segments.size > 0;
    }
    st.select({ keys: new Set([id]), primary: { kind: 'key', id } }, 'replace');
    return false;
  };

  const endPress = (commit: boolean): void => {
    const p = press;
    press = null;
    if (p === null) return;
    session.frozenRanges.delete(p.laneKey);
    session.drag = null;
    if (p.gesture !== null) {
      if (commit) p.gesture.commit();
      else p.gesture.cancel();
    }
    svc().ui.setGestureHints(null);
    session.touch();
    session.invalidate('content');
    session.invalidate('overlay', p.view.id);
  };

  const beginDrag = (p: PressState): boolean => {
    const store = svc().store;
    if (store === null || store.gestureActive) return false;
    const range = p.view.scene()?.lanes[p.lane]?.range ?? null;
    if (range !== null) session.frozenRanges.set(p.laneKey, range);
    p.gesture = store.beginGesture({
      label: ref(m.txMoveKeys, { count: Math.max(1, p.keys.length) }),
      command: 'keys.drag',
    });
    const altFree = altFreeChord(svc(), { ctrl: true, shift: true });
    svc().ui.setGestureHints([
      { chord: 'Shift', label: ref(m.dragHintLock) },
      { chord: altFree ? 'Ctrl+Shift' : 'Alt', label: ref(m.dragHintNoSnap) },
      { chord: 'Ctrl', label: ref(m.dragHintRound) },
      { chord: 'Escape', label: ref(m.dragHintCancel) },
    ]);
    return true;
  };

  const dragTo = (p: PressState, e: TimelinePointerEvent): void => {
    const s = svc();
    const store = s.store;
    if (store === null || p.gesture === null) return;
    const dxPx = e.x - p.x0;
    const dyPx = e.y - p.y0;
    // Alt-free: Ctrl+Shift is the "no snap" chord, not the axis lock plus rounding.
    const chord = altFreeChord(s, e);
    const shift = e.shift && !chord;
    const ctrl = e.ctrl && !chord;
    if (shift && p.lock === null && Math.hypot(dxPx, dyPx) >= LOCK_PX)
      p.lock = Math.abs(dxPx) > Math.abs(dyPx) ? 'value' : 'time';
    if (!shift) p.lock = null;

    // Time.
    let dt: Beat = Beats.ZERO;
    // The drag's snap step: a moved key stops one step before an unmoved neighbour (moveKeys `minGap`).
    let gapDiv = snapDiv(s);
    if (p.mode !== 'none' && p.grabbedBeat !== null && p.lock !== 'value') {
      const div = noSnapHeld(s, e) ? FINEST_DIV : snapDiv(s);
      gapDiv = div;
      const target = Math.max(0, Beats.toF64(p.grabbedBeat) + (e.beat - p.beat0));
      const snapped = snapBeat(target, div);
      dt = clampMove(p.room, Beats.sub(snapped, p.grabbedBeat), div);
    }
    // Value.
    let dv = 0;
    if (p.map !== null && p.lock !== 'time') {
      const raw = p.map.toValue(e.x) - p.map.toValue(p.x0);
      dv = raw;
    }
    const step = p.map === null ? 1 : ctrl ? niceStep(p.map.perPx * 12) : niceStep(p.map.perPx);
    const edits: Array<{ key: KeyId; part: 'in' | 'out' | 'both'; value: TrackValue }> = [];
    if (dv !== 0) {
      for (const vk of p.valueKeys) {
        if (p.mode === 'in') edits.push({ key: vk.key, part: 'in', value: roundTo(vk.vIn + dv, step) });
        else if (p.mode === 'out') edits.push({ key: vk.key, part: 'out', value: roundTo(vk.vOut + dv, step) });
        else if (vk.jump) {
          edits.push({ key: vk.key, part: 'in', value: roundTo(vk.vIn + dv, step) });
          edits.push({ key: vk.key, part: 'out', value: roundTo(vk.vOut + dv, step) });
        } else edits.push({ key: vk.key, part: 'both', value: roundTo(vk.vOut + dv, step) });
      }
    }
    const moveDt = dt;
    const out = p.gesture.update((tx) => {
      if (!Beats.eq(moveDt, Beats.ZERO))
        Core.keys.moveKeys(tx, { keys: p.keys, dt: moveDt, minGap: Beats.make(0, 1, gapDiv) });
      for (const ed of edits) {
        if (tx.doc.keys.has(ed.key)) Core.keys.setKeyValues(tx, { keys: [ed.key], part: ed.part, value: ed.value });
      }
    });
    if (!out.ok) return;
    // Readout of the grabbed key (after the core's clamping).
    const g = p.grabbed === null ? undefined : store.doc.keys.get(p.grabbed);
    const spec = p.spec;
    const lane = spec.lanes[p.lane];
    if (g !== undefined && lane !== undefined) {
      const v = p.mode === 'in' ? g.vIn : g.vOut;
      const num = typeof v === 'number' ? v : Number.NaN;
      const text = s.i18n.t(m.dragReadout, {
        beat: formatBeat(g.t),
        value: Number.isFinite(num) ? formatValue((k, pr) => s.i18n.t(k, pr), lane.prop, num, spec) : '',
      });
      session.drag = { instanceId: p.view.id, lane: p.lane, beat: Beats.toF64(g.t), value: num, text };
    }
    session.invalidate('overlay', p.view.id);
  };

  const handlers: MotionColumnHandlers = {
    pointerDown(view, e, hit) {
      const s = svc();
      const store = s.store;
      const spec = deps.specOf(view.id);
      if (store === null || spec === null) return;
      if ((e.buttons & 2) !== 0) return;
      const lanes = view.lanes();
      const laneIdx = hit !== null && hit.lane >= 0 ? hit.lane : laneAtX(lanes, e.x);
      if (laneIdx >= 0) focusColumn(view, spec, laneIdx);
      const st = s.editor.getState();
      if (st.placement?.kind === 'segment') st.setPlacement(null);
      if (hit === null || hit.kind === 'empty' || hit.key === undefined) {
        if (press !== null) endPress(false);
        const host = session.timeline();
        host?.capturePointer(view.id);
        return;
      }
      const collapse = selectHit(hit, e);
      if (hit.kind === 'segment' || e.ctrl) {
        press = null;
        return;
      }
      const mode = hit.kind === 'keyIn' ? 'in' : hit.kind === 'keyOut' ? 'out' : 'keys';
      const lane = spec.lanes[laneIdx];
      const rect = lanes[laneIdx];
      const grabbedRec = store.doc.keys.get(hit.key);
      if (lane === undefined || grabbedRec === undefined) return;
      const after = s.editor.getState().selection;
      // Every drag moves the selected keys in time (a half-diamond too: vertical = time for any key).
      const keys = [...after.keys].filter((id) => store.doc.keys.has(id));
      if (!keys.includes(hit.key)) keys.push(hit.key);
      // Value edits: the dragged lane's keys among the moved ones (numeric lanes only).
      const target = laneRef(spec, lane);
      const track = laneTrack(store, target);
      const valueKeys =
        rect?.map === null || rect === undefined
          ? []
          : keys
              .map((id) => store.doc.keys.get(id))
              .filter(
                (k): k is NonNullable<typeof k> =>
                  k !== undefined && k.track === track && typeof k.vIn === 'number' && typeof k.vOut === 'number',
              )
              .filter((k) => mode === 'keys' || k.id === hit.key)
              .map((k) => {
                const prev = store.idx.prevKey(k.id);
                return {
                  key: k.id,
                  vIn: k.vIn as number,
                  vOut: k.vOut as number,
                  jump: prev !== undefined && k.vIn !== k.vOut,
                };
              });
      press = {
        view,
        spec,
        lane: laneIdx,
        hit,
        x0: e.x,
        y0: e.y,
        beat0: e.beat,
        mode,
        grabbed: hit.key,
        grabbedBeat: grabbedRec.t,
        keys,
        room: moveRoom(store, keys),
        valueKeys,
        map: rect?.map ?? null,
        laneKey: laneKeyOf(target.line, target.prop, target.layer),
        collapseOnClick: collapse,
        moved: false,
        lock: null,
        gesture: null,
      };
    },

    pointerMove(view, e) {
      const p = press;
      if (p === null || p.view !== view || (e.buttons & 1) === 0) {
        if (p === null || (e.buttons & 1) === 0) setHover(view, e);
        return;
      }
      if (!p.moved) {
        if (Math.hypot(e.x - p.x0, e.y - p.y0) < DRAG_PX) return;
        if (p.mode === 'none') return;
        const store = svc().store;
        const moving = p.keys;
        if (store !== null && guidedPending(svc(), session, refsOfKeys(store, moving)).size > 0) {
          // Guided Σ column: the first edit asks first; drag again after "Edit the base layer".
          press = null;
          void confirmGuidedKeys(svc(), session, moving);
          return;
        }
        if (!beginDrag(p)) return;
        p.moved = true;
        session.hover = null;
      }
      dragTo(p, e);
    },

    pointerUp(view) {
      const p = press;
      if (p === null || p.view !== view) return;
      if (!p.moved && p.collapseOnClick && p.grabbed !== null) {
        const id = p.grabbed;
        svc()
          .editor.getState()
          .select({ keys: new Set([id]), primary: { kind: 'key', id } }, 'replace');
      }
      endPress(true);
    },

    doubleClick(view, e, hit) {
      const s = svc();
      const store = s.store;
      const spec = deps.specOf(view.id);
      if (store === null || spec === null) return;
      if (press !== null) endPress(true);
      if (hit !== null && hit.kind === 'segment' && hit.key !== undefined) {
        const id = hit.key;
        s.editor.getState().select({ segments: new Set([id]), primary: { kind: 'segment', id } }, 'replace');
        void openEasingPicker(s, session, [id]);
        return;
      }
      if (hit !== null && hit.key !== undefined && hit.kind !== 'empty') {
        void s.registry.run('inspector.focusField', {
          field: hit.kind === 'keyIn' ? 'key.vIn' : hit.kind === 'keyOut' ? 'key.vOut' : 'key.value',
        });
        return;
      }
      const li = hit !== null && hit.lane >= 0 ? hit.lane : laneAtX(view.lanes(), e.x);
      const lane = spec.lanes[li];
      if (lane === undefined) return;
      const target: ColumnRef = laneRef(spec, lane);
      const beat = snapBeat(Math.max(0, e.beat), noSnapHeld(s, e) ? FINEST_DIV : snapDiv(s));
      void (async () => {
        if (!(await confirmGuidedEdit(s, session, spec))) return;
        addKeys(s, session, [target], beat);
      })();
    },

    contextMenu(view, e, hit, client) {
      const s = svc();
      const spec = deps.specOf(view.id);
      if (spec === null || s.store === null) return;
      const lanes = view.lanes();
      const li = hit !== null && hit.lane >= 0 ? hit.lane : laneAtX(lanes, e.x);
      if (li >= 0) focusColumn(view, spec, li);
      setHover(view, e);
      if (hit !== null && hit.key !== undefined && hit.kind !== 'empty') {
        const sel = s.editor.getState().selection;
        const id = hit.key;
        const selected = hit.kind === 'segment' ? sel.segments.has(id) : sel.keys.has(id);
        if (!selected) {
          if (hit.kind === 'segment')
            s.editor.getState().select({ segments: new Set([id]), primary: { kind: 'segment', id } }, 'replace');
          else s.editor.getState().select({ keys: new Set([id]), primary: { kind: 'key', id } }, 'replace');
        }
      }
      if (client !== null)
        s.ui.openContextMenu?.('motion.context', client.x, client.y, contextEvent(client.x, client.y));
    },

    wheel(_view, e, _axis: TimeAxis) {
      const s = svc();
      const store = s.store;
      if (store === null || s.keymap.preset !== 'rpe') return false;
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl || e.shiftKey || e.altKey) return false;
      const pinch = e.ctrlKey && e.deltaMode === 0 && !Number.isInteger(e.deltaY);
      if (pinch) return false;
      const sel = s.editor.getState().selection;
      const keys = keysOrSegments(store, sel);
      if (keys.length === 0) return false;
      const dy = e.deltaMode === 1 ? e.deltaY * 40 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
      const steps = dy === 0 ? 0 : -Math.sign(dy) * Math.max(1, Math.round(Math.abs(dy) / 100));
      if (steps === 0) return true;
      if (guidedPending(s, session, refsOfKeys(store, keys)).size > 0) {
        void confirmGuidedKeys(s, session, keys);
        return true;
      }
      fineTune(session, keys, steps);
      return true;
    },

    zoomX(view, factor, x) {
      const scene = view.scene();
      const rects = view.lanes();
      const li = laneAtX(rects, x);
      const map = rects[li]?.map ?? null;
      const lane = scene?.lanes[li];
      if (map === null || lane === undefined) return;
      const next = zoomValueRange(map.lo, map.hi, factor, map.toValue(x));
      if (next === null) return;
      session.lockedRanges.set(lane.key, next);
      session.touch();
      session.invalidate('content', view.id);
      session.invalidate('overlay', view.id);
    },

    panX(view, dxPx) {
      const scene = view.scene();
      if (scene === null || !Number.isFinite(dxPx) || dxPx === 0) return;
      let changed = false;
      const rects = view.lanes();
      for (let i = 0; i < rects.length; i++) {
        const map = rects[i]?.map ?? null;
        const lane = scene.lanes[i];
        if (map === null || lane === undefined) continue;
        // Positive dx moves the view right: larger values come into view.
        const shift = dxPx * map.perPx;
        session.lockedRanges.set(lane.key, { lo: map.lo + shift, hi: map.hi + shift });
        changed = true;
      }
      if (!changed) return;
      session.touch();
      session.invalidate('content', view.id);
      session.invalidate('overlay', view.id);
    },

    pointerLeave(view) {
      if (session.hover?.instanceId === view.id) {
        session.hover = null;
        session.invalidate('overlay', view.id);
      }
    },

    pointerCancel(view) {
      if (press?.view === view) endPress(false);
    },

    disposed(view) {
      if (press?.view === view) endPress(false);
      if (session.hover?.instanceId === view.id) session.hover = null;
    },
  };
  return handlers;
}

/** The right click as a PointerEvent (context menu commands act where the menu was opened). */
function contextEvent(x: number, y: number): PointerEvent | null {
  try {
    return new PointerEvent('contextmenu', { clientX: x, clientY: y, button: 2 });
  } catch {
    return null;
  }
}

/** RPE Ctrl+wheel steps per property (EVT-11 defaults with "Event scroll speed" 10). */
export function fineTuneStep(prop: string): number {
  switch (prop) {
    case 'moveX':
    case 'moveY':
    case 'alpha':
      return 10;
    case 'rotate':
      return 0.25;
    case 'speed':
    case 'scaleX':
    case 'scaleY':
      return 0.1;
    case 'gif':
      return 0.01;
    default:
      return 1;
  }
}

/** Fine-tunes keyframe values by `steps` RPE wheel steps (one undoable step per burst, coalesced). */
export function fineTune(session: MotionSession, keys: readonly KeyId[], steps: number): boolean {
  const s = session.services;
  const store = s.store;
  if (store === null) return false;
  const sel = s.editor.getState().selection;
  const segEnds = new Set<KeyId>();
  for (const id of sel.segments) {
    const n = store.idx.nextKey(id);
    if (n !== undefined && !sel.keys.has(id)) segEnds.add(n);
  }
  const edits: Array<{ key: KeyId; part: 'in' | 'out' | 'both'; value: number }> = [];
  for (const id of keys) {
    const k = store.doc.keys.get(id);
    const tr = k === undefined ? undefined : store.doc.tracks.get(k.track);
    if (k === undefined || tr?.owner.kind !== 'line' || typeof k.vIn !== 'number' || typeof k.vOut !== 'number')
      continue;
    if (sel.segments.has(id) && !sel.keys.has(id) && !segEnds.has(id)) continue;
    const step = fineTuneStep(tr.owner.prop) * steps;
    const jump = store.idx.prevKey(id) !== undefined && k.vIn !== k.vOut;
    // The property's domain bounds the tune (UX-042: opacity stops at 255 / 0, GIF progress at 1 / 0).
    const prop = tr.owner.prop;
    const tuned = (v: number): number => roundTo(clampToDomain(prop, v, v + step), 1e-6);
    if (segEnds.has(id)) {
      if (tuned(k.vIn) !== k.vIn) edits.push({ key: id, part: jump ? 'in' : 'both', value: tuned(k.vIn) });
    } else if (tuned(k.vOut) !== k.vOut) edits.push({ key: id, part: jump ? 'out' : 'both', value: tuned(k.vOut) });
  }
  if (edits.length === 0) return false;
  const selKey = [...keys].sort((a, b) => a - b).join(',');
  const out = runTx(
    s,
    { label: ref(m.txFineTune), command: 'motion.fineTune', coalesceKey: `wheel:${selKey}` },
    (tx) => {
      for (const ed of edits) Core.keys.setKeyValues(tx, { keys: [ed.key], part: ed.part, value: ed.value });
    },
  );
  return out.ok;
}

/** Values one friendly step away (nudges); exported for the commands. */
export function nudgeStepOf(session: MotionSession, key: KeyId): number {
  const store = session.services.store;
  if (store === null) return 1;
  const k = store.doc.keys.get(key);
  const tr = k === undefined ? undefined : store.doc.tracks.get(k.track);
  if (tr?.owner.kind !== 'line') return 1;
  return valueStep(tr.owner.prop, propCtxOf(store, tr.owner.line));
}
